import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Eye, HandCoins, Loader2, Printer, ScanLine, Undo2, Warehouse, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusPill } from '@/components/Status';
import { type StatusTone } from '@/lib/status';
import { NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { Can } from '@/auth/guards';
import { useAuth } from '@/auth/useAuth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getLiveProductLocations,
  setDiscrepancyDispositions,
  type CountDiscrepancy,
  type Disposition,
  type OrdersOverview,
  type OverviewReceiving,
} from '@/api/orders';
import { ProductCell, ProductIdentity } from './ProductLink';
import { PrintableCountSheet } from './PrintableCountSheet';
import { DISCREPANCY_KIND_LABELS, fmtDateLong, money } from './lib';
import {
  AcotReceivingLink, JourneyName, RowAction, WorkEmpty, WorkFootnote, WorkNotice, WorkRow,
  WorkSection, WorkSegmented, WorkTable, WorkToolbar,
} from './workLib';

const RESOLUTION_LABELS: Record<string, string> = {
  pending: 'open — not yet requested',
  requested: 'requested — watching',
  resolved: 'credit landed',
};

/** A credit's state: landed is done, requested is with the vendor, pending still needs asking. */
const RESOLUTION_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  requested: 'info',
  resolved: 'success',
};

/** A ruling's state: a credit is settled, a recount/recapture is out with
 *  someone, the rest (not this order, let go) are plain facts. */
function dispositionTone(disposition: Disposition | null): StatusTone {
  if (disposition === 'credited') return 'success';
  if (disposition === 'recount' || disposition === 'recapture') return 'info';
  return 'neutral';
}

type Row = CountDiscrepancy & { rcv: OverviewReceiving };

/**
 * `active` (the default): what is still waiting on someone — undecided rows
 * and recounts out with the warehouse. A recount used to drop out of the
 * default view the moment it was recorded and so out of sight for good.
 */
export type DiscrepancyScope = 'active' | 'open' | 'undecided' | 'all';

/** "5 days" / "9 weeks" since a timestamp. */
function age(at: string): string {
  const days = Math.max(0, Math.floor((Date.now() - new Date(at).getTime()) / 86_400_000));
  if (days < 14) return `${days} day${days === 1 ? '' : 's'}`;
  return `${Math.floor(days / 7)} weeks`;
}

/**
 * What prints on the count sheet: every recount on an open receiving, whatever
 * the screen is filtered to (the instruction was already given — it must not
 * hinge on a dropdown), plus the settle rows in view on open receivings. Once
 * balancedAt is set the money is closed and no count changes it; and
 * `recapture` never prints — that verdict says the INVOICE was misread, so the
 * shelf is not in question.
 */
function countSheetRows(allRows: Row[], shown: Row[]): Row[] {
  const recounts = allRows.filter((r) => r.action === 'recount' && !r.rcv.balancedAt);
  const settle = shown.filter((r) => r.action === 'settle' && !r.rcv.balancedAt);
  return [...recounts, ...settle];
}
type Grouping = 'receiving' | 'items';

/**
 * What the table holds: rows Matt has to decide, plus the ones he has already
 * handed off. A hand-off stays visible — it is his to track and his to take
 * back — where a row that was never his (an unread invoice, a shipment in
 * transit) belongs in the routed summaries below instead.
 */
const TABLE_ACTIONS = new Set(['settle', 'recount', 'recapture']);

/** In the table: Matt's to settle, or handed off BY him. A row routed to the
 *  orders run by its cause (an exact pack multiple) was never his — it
 *  counts in the "with the orders run" line, not here. */
const inTable = (r: Row) => TABLE_ACTIONS.has(r.action)
  && (r.action === 'settle' || r.dispositionSource === 'recorded');

/** Decisions that keep working: credit watched, move pending, or out with someone. */
const isOpenFollowUp = (r: Row) =>
  (r.disposition === 'credited' && (r.resolutionStatus === 'pending' || r.resolutionStatus === 'requested'))
  || (r.disposition === 'excluded' && r.resolutionStatus === 'pending')
  || r.disposition === 'recount' || r.disposition === 'recapture';

/**
 * What a recapture hands the orders run. The row itself carries the evidence —
 * an exact multiple, usually with the multiplier printed in the description —
 * so the note records the observation rather than making Matt retype it.
 */
function recaptureNote(row: Row): string | null {
  const { qtyInvoiced: inv, qtyReceived: rec } = row;
  if (!inv || !rec) return null;
  const hi = Math.max(inv, rec);
  const lo = Math.min(inv, rec);
  if (hi % lo !== 0 || hi / lo < 2) return null;
  return `Pack grain: invoiced ${inv} vs received ${rec} (exact x${hi / lo}). `
    + `Re-read this product on the invoice and re-register it at the pid's grain.`;
}

/**
 * The decision buttons. "Recount" sends it back to the warehouse — maybe WE
 * miscounted; costing stays blocked until the count is verified. "Get credit"
 * keeps the line open while the orders run watches for the claim and the
 * credit. "Let it go" absorbs the value into what arrived.
 *
 * "Recapture" is recount's sibling, pointed at the orders run instead of the
 * warehouse: the numbers disagree because the INVOICE was read wrong, most
 * often at the wrong grain — it bills one "(x10)" pack and acot-db books ten
 * eaches, so the line reads +9 over and nothing is actually wrong. Exact
 * multiples are routed to the run by the server without asking; this button is
 * for the mis-reads the arithmetic cannot spot.
 *
 * "Not this order" is deliberately absent: a misfiled line is detected by
 * pairing it against the receiving holding its counterpart, not by asking Matt
 * to label it, and it is answered by moving the line in the backend rather than
 * by a disposition here.
 */
function DecideButtons({ row, onDecide, busy }: {
  row: Row;
  onDecide: (row: Row, disposition: Disposition | null) => void;
  busy: boolean;
}) {
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : (
        <>
          <Hint content="The invoice was read wrong, usually at the wrong pack grain; sends it back to the run for a re-read.">
            <RowAction
              icon={<ScanLine className="h-3 w-3" />}
              onClick={() => onDecide(row, 'recapture')}
            >
              Recapture
            </RowAction>
          </Hint>
          <Hint content="Have the warehouse verify the count; costing waits.">
            <RowAction
              variant="primary"
              icon={<Warehouse className="h-3 w-3" />}
              onClick={() => onDecide(row, 'recount')}
            >
              Recount
            </RowAction>
          </Hint>
          <Hint content="The supplier owes a credit; the run watches for it.">
            <RowAction
              variant="primary"
              icon={<HandCoins className="h-3 w-3" />}
              onClick={() => onDecide(row, 'credited')}
            >
              Get credit
            </RowAction>
          </Hint>
          <Hint content="Not worth chasing; the value spreads over what arrived.">
            <RowAction variant="quiet" onClick={() => onDecide(row, 'absorbed')}>
              Let it go
            </RowAction>
          </Hint>
        </>
      )}
    </span>
  );
}

function DecisionCell({ row, onDecide, busy, readOnly }: {
  row: Row;
  onDecide: (row: Row, disposition: Disposition | null) => void;
  busy: boolean;
  readOnly: boolean;
}) {
  if (row.dispositionSource === 'undecided') {
    // A recount that came back: the count is the evidence for this decision.
    const counted = row.counted && (
      <span className="inline-flex flex-wrap items-center gap-1">
        <Hint content={row.dispositionAt ? `Recount came back ${fmtDateLong(row.dispositionAt)}` : 'Recount came back'}>
          <StatusPill tone="info" icon={<Warehouse />}>counted</StatusPill>
        </Hint>
        {!readOnly && row.dispositionNote && <NoteDisclosure text={row.dispositionNote} className="basis-full" />}
      </span>
    );
    if (readOnly) return counted || <span className="text-muted-foreground">—</span>;
    if (counted) {
      return (
        <span className="grid justify-items-end gap-1">
          <Can code="orders:write:balance"><DecideButtons row={row} onDecide={onDecide} busy={busy} /></Can>
          {counted}
        </span>
      );
    }
    // POST /receipts/:id/discrepancies — orders:write:balance. A viewer of
    // the page sees the undecided row and no buttons.
    return <Can code="orders:write:balance"><DecideButtons row={row} onDecide={onDecide} busy={busy} /></Can>;
  }
  const auto = row.dispositionSource === 'inferred';
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <StatusPill tone={dispositionTone(row.disposition)} className={cn(auto && 'opacity-60')}>
        {row.disposition === 'excluded' ? 'not this order'
          : row.disposition === 'absorbed' ? 'let go'
          : row.disposition === 'as_booked' ? 'closed as booked'
          : row.disposition === 'recount' ? `recount${row.dispositionAt ? ` · ${age(row.dispositionAt)}` : ''}`
          : row.disposition === 'recapture' ? 'recapture'
          : row.disposition}
        {row.creditAmount != null && ` ${money(row.creditAmount, row.rcv.currency)}`}
        {auto && ' (auto)'}
      </StatusPill>
      {row.disposition === 'credited' && row.resolutionStatus && (
        <StatusPill
          tone={RESOLUTION_TONE[row.resolutionStatus] ?? 'neutral'}
          icon={row.resolutionStatus !== 'resolved' ? <Eye /> : undefined}
        >
          {RESOLUTION_LABELS[row.resolutionStatus]}
        </StatusPill>
      )}
      {row.disposition === 'excluded' && row.resolutionStatus && (
        <StatusPill
          tone={row.resolutionStatus === 'resolved' ? 'success' : 'warning'}
          icon={row.resolutionStatus === 'resolved' ? undefined : <Undo2 />}
        >
          {row.resolutionStatus === 'resolved' ? 'moved' : 'move to correct receiving'}
        </StatusPill>
      )}
      {!auto && !readOnly && (busy ? (
        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
      ) : (
        <Can code="orders:write:balance">
          <Hint content="Clear the decision.">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onDecide(row, null)}
            >
              <X className="h-3 w-3" />
            </button>
          </Hint>
        </Can>
      ))}
      {!auto && !readOnly && row.dispositionNote && (
        <NoteDisclosure text={row.dispositionNote} className="basis-full" />
      )}
    </span>
  );
}

/**
 * Fixed column widths so every table on the page lines up vertically —
 * per-receiving tables otherwise each auto-size to their own content.
 */
/** The receiving number: a link into the acot backend, or plain text read-only. */
function ReceivingRef({ receivingId, vendor, readOnly, className }: {
  receivingId: number;
  vendor?: string | null;
  readOnly: boolean;
  className?: string;
}) {
  if (!readOnly) return <AcotReceivingLink receivingId={receivingId} vendor={vendor} className={className} />;
  return <span className={cn('text-xs font-medium tabular-nums', className)}>#{receivingId}{vendor ? ` ${vendor}` : ''}</span>;
}

function DiscrepancyTable({ rows, showReceiving, onDecide, busyKeys, readOnly, showValue }: {
  rows: Row[];
  showReceiving: boolean;
  onDecide: (row: Row, disposition: Disposition | null) => void;
  busyKeys: Set<string>;
  readOnly: boolean;
  /** The value column needs `orders:read:costs`; without it every value is null. */
  showValue: boolean;
}) {
  return (
    <WorkTable
      cols={[undefined, ...(showReceiving ? ['w-36'] : []), 'w-14', 'w-14', 'w-32', ...(showValue ? ['w-24'] : []), readOnly ? 'w-56' : 'w-[26rem]']}
      minWidth={readOnly ? '720px' : '960px'}
      head={
        <>
          <th>Item</th>
          {showReceiving && <th>Receiving</th>}
          <th className="num"><Hint content="What the invoice bills">Inv.</Hint></th>
          <th className="num"><Hint content="What the warehouse booked">Rec.</Hint></th>
          <th>Kind</th>
          {showValue && <th className="num"><Hint content="Value of the difference at landed cost">Value</Hint></th>}
          <th>Decision</th>
        </>
      }
    >
      {rows.map((row) => (
        <tr key={`${row.rcv.receiptId}:${row.key}`}>
          <td>
            <ProductCell desc={row.desc} pid={row.pid} itemnumber={row.itemnumber} vendorSku={row.vendorSku} linked={!readOnly} />
          </td>
          {showReceiving && (
            <td>
              <div className="truncate">{row.rcv.vendor}</div>
              <ReceivingRef receivingId={row.rcv.receivingId} readOnly={readOnly} className="text-muted-foreground" />
            </td>
          )}
          <td className="num">{row.qtyInvoiced}</td>
          <td className="num">{row.qtyReceived}</td>
          <td className="whitespace-nowrap">
            <StatusPill>
              {DISCREPANCY_KIND_LABELS[row.kind] || row.kind} {row.difference > 0 ? `+${row.difference}` : row.difference}
            </StatusPill>
          </td>
          {showValue && (
            <td className="num">
              {row.valueAtTarget == null ? '—' : money(row.valueAtTarget, row.rcv.currency)}
            </td>
          )}
          <td>
            <DecisionCell row={row} onDecide={onDecide} busy={busyKeys.has(`${row.rcv.receiptId}:${row.key}`)} readOnly={readOnly} />
          </td>
        </tr>
      ))}
    </WorkTable>
  );
}

/**
 * The Decide view: count differences that need a decision from Matt — recount
 * first, then credit vs let-it-go — and nothing else.
 *
 * Everything else the three documents disagree about routes to its OWN view
 * now (Move, Documents, Follow-ups); the leftovers that are nobody's (in
 * transit, never-itemised, with the orders run) get one info line at the foot.
 * The "Investigate" section is the one judgment call that stays: received
 * units no invoice line bills.
 *
 * `readOnly` is the warehouse page's copy: the same rows and filters, the
 * count-sheet button, and nothing else — no decisions, no journey or backend
 * links, no routing footnote.
 */
type DiscrepanciesViewProps = {
  overview: OrdersOverview;
  initialScope?: DiscrepancyScope;
} & (
  | {
    readOnly?: false;
    onChanged: () => void;
    /** Jump to the journey in All orders — every work view offers this. */
    onOpenJourney: (orderId: string) => void;
  }
  | { readOnly: true; onChanged?: never; onOpenJourney?: never }
);

export function DiscrepanciesView({
  overview,
  onChanged,
  onOpenJourney,
  initialScope = 'active',
  readOnly = false,
}: DiscrepanciesViewProps) {
  const { can } = useAuth();
  const showValue = can('orders:read:costs');
  const [scope, setScope] = useState<DiscrepancyScope>(initialScope);
  const [kind, setKind] = useState<string>('all');
  const [grouping, setGrouping] = useState<Grouping>('receiving');
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  /** Rows + LIVE locations, snapshotted at click time. The sheet mounts only
   *  once locations have arrived — printing with stale ones walks someone to
   *  the wrong shelf, so a failed fetch surfaces as an error and no sheet. */
  // ?receiving=<acot receiving id> — Today's Decide link: one receiving's rows.
  // The warehouse copy ignores it.
  const [searchParams, setSearchParams] = useSearchParams();
  const receivingFilter = readOnly ? null : searchParams.get('receiving');
  const clearReceivingFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('receiving');
    setSearchParams(next, { replace: true });
  };
  const [printSheet, setPrintSheet] = useState<{ rows: Row[]; locations: Record<string, string> } | null>(null);
  const [fetchingSheet, setFetchingSheet] = useState(false);

  const allRows = useMemo<Row[]>(
    () => overview.receivings
      .filter((rcv) => !receivingFilter || String(rcv.receivingId) === receivingFilter)
      .flatMap((rcv) => rcv.discrepancies.map((d) => ({ ...d, rcv }))),
    [overview, receivingFilter]
  );

  /** The table's population: decisions, and the hand-offs made from them. */
  const settleRows = useMemo(
    () => allRows.filter(inTable), [allRows]);

  const rows = useMemo(() => {
    let out = settleRows;
    if (scope === 'active') {
      out = out.filter((r) => r.dispositionSource === 'undecided' || r.action === 'recount');
    } else if (scope === 'open') {
      out = out.filter((r) => !r.rcv.balancedAt || isOpenFollowUp(r));
    } else if (scope === 'undecided') {
      out = out.filter((r) => r.dispositionSource === 'undecided');
    }
    if (kind !== 'all') out = out.filter((r) => r.kind === kind);
    return [...out].sort((a, b) => Math.abs(b.valueAtTarget ?? 0) - Math.abs(a.valueAtTarget ?? 0));
  }, [settleRows, scope, kind]);

  /**
   * Worst first. Grouping used to order by receiving id — newest first — which
   * is why the largest block of unanswerable rows greeted you at the top of the
   * page regardless of what it was worth.
   */
  const byReceiving = useMemo(() => {
    const map = new Map<string, { rcv: OverviewReceiving; rows: Row[]; value: number }>();
    for (const row of rows) {
      const entry = map.get(row.rcv.receiptId) || { rcv: row.rcv, rows: [], value: 0 };
      entry.rows.push(row);
      entry.value += Math.abs(row.valueAtTarget ?? 0);
      map.set(row.rcv.receiptId, entry);
    }
    return [...map.values()].sort((a, b) => b.value - a.value || b.rcv.receivingId - a.rcv.receivingId);
  }, [rows]);

  /** The judgment calls that stay here: received units no invoice line bills.
   *  Undecided only — a ruled-on row routes itself (absorbed settles, excluded
   *  lands in Move, recapture lands with the orders run). */
  const investigate = useMemo(
    () => allRows.filter((r) => r.action === 'review' && r.dispositionSource === 'undecided')
      .sort((a, b) => Math.abs(b.valueAtTarget ?? 0) - Math.abs(a.valueAtTarget ?? 0)),
    [allRows]
  );

  /** Nobody's rows — one honest info line so the table's silence isn't suspicious. */
  const elsewhere = useMemo(() => ({
    waiting: allRows.filter((r) => r.action === 'wait').length,
    agent: allRows.filter((r) => r.action === 'capture_lines' || r.action === 'recapture').length,
    accepted: allRows.filter((r) => r.action === 'accept').length,
  }), [allRows]);

  const undecidedCount = settleRows.filter((r) => r.dispositionSource === 'undecided').length;
  const sheetRows = useMemo(() => countSheetRows(settleRows, rows), [settleRows, rows]);
  const recountCount = sheetRows.filter((r) => r.action === 'recount').length;

  const decide = async (
    row: Row,
    disposition: Disposition | null,
    opts: { resolutionStatus?: 'pending'; note?: string | null } = {}
  ) => {
    const busyKey = `${row.rcv.receiptId}:${row.key}`;
    setBusyKeys((s) => new Set(s).add(busyKey));
    setError(null);
    try {
      await setDiscrepancyDispositions(row.rcv.receiptId, [{
        line_key: row.key,
        disposition,
        pid: row.pid,
        // A credit starts open: the orders run watches until it lands.
        // (Recount and recapture need no status — the disposition IS the open state.)
        ...(disposition === 'credited' || opts.resolutionStatus
          ? { resolution_status: 'pending' as const } : {}),
        // Hand the orders run the observation that prompted the recapture, so
        // it knows what to look for in the document.
        ...(opts.note !== undefined
          ? { note: opts.note }
          : disposition === 'recapture' ? { note: recaptureNote(row) } : {}),
      }]);
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save decision');
    } finally {
      setBusyKeys((s) => { const next = new Set(s); next.delete(busyKey); return next; });
    }
  };

  /**
   * Fetch CURRENT shelf locations, then open the print sheet. Rows are
   * snapshotted here so a filter change mid-fetch cannot alter what prints.
   * Live-only on purpose: the mirrored location is a snapshot from the last
   * orders run, and a sheet that walks someone to a stale shelf is worse than
   * an error asking to try again.
   */
  const printCountSheet = async () => {
    const snapshot = sheetRows;
    if (!snapshot.length) {
      setError('Nothing for the warehouse to count.');
      return;
    }
    const pids = [...new Set(snapshot.map((r) => r.pid).filter((p): p is number => p != null))];
    setFetchingSheet(true);
    setError(null);
    try {
      const locations = pids.length ? await getLiveProductLocations(pids) : {};
      setPrintSheet({ rows: snapshot, locations });
    } catch (e) {
      setError(`Count sheet: ${e instanceof Error ? e.message : 'failed to fetch live locations'}`);
    } finally {
      setFetchingSheet(false);
    }
  };

  return (
    <div>
      <WorkToolbar>
        <WorkSegmented<Grouping>
          label="Grouping"
          value={grouping}
          onChange={setGrouping}
          options={[
            { value: 'receiving', label: 'By receiving' },
            { value: 'items', label: 'All items' },
          ]}
        />
        <Select value={scope} onValueChange={(v) => setScope(v as DiscrepancyScope)}>
          <SelectTrigger aria-label="Scope" className="w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">To decide &amp; recount</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="undecided">Undecided only</SelectItem>
            <SelectItem value="all">Everything</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger aria-label="Kind" className="w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="short">Short</SelectItem>
            <SelectItem value="over">Over</SelectItem>
            <SelectItem value="not_received">Not received</SelectItem>
          </SelectContent>
        </Select>
        {receivingFilter && (
          <Hint content="Show every receiving">
            <RowAction variant="secondary" icon={<X className="h-3 w-3" />} onClick={clearReceivingFilter}>
              Receiving #{receivingFilter}
            </RowAction>
          </Hint>
        )}
        {/* The sheet is its own page (orders:page:count_sheet): the warehouse
            account holds it, and a discrepancies viewer need not. */}
        <Can code="orders:page:count_sheet">
          <Hint content="Print a count sheet for the warehouse, sorted by current shelf location.">
            <RowAction
              variant="quiet"
              icon={<Printer className="h-3.5 w-3.5" />}
              busy={fetchingSheet}
              disabled={sheetRows.length === 0 || fetchingSheet}
              onClick={printCountSheet}
            >
              Count sheet
            </RowAction>
          </Hint>
        </Can>
        <span className="ml-auto text-xs text-muted-foreground">
          {rows.length} shown{!readOnly && ` · ${undecidedCount} to decide`}{recountCount > 0 && ` · ${recountCount} to recount`}
        </span>
      </WorkToolbar>

      {printSheet && (
        <PrintableCountSheet
          rows={printSheet.rows}
          locations={printSheet.locations}
          onDone={() => setPrintSheet(null)}
        />
      )}

      {error && <WorkNotice tone="danger">{error}</WorkNotice>}

      {rows.length === 0 ? (
        <WorkEmpty compact>{readOnly ? 'No discrepancies' : 'Nothing to decide'}{scope !== 'all' ? ' in this view' : ''}.</WorkEmpty>
      ) : grouping === 'items' ? (
        <DiscrepancyTable rows={rows} showReceiving onDecide={decide} busyKeys={busyKeys} readOnly={readOnly} showValue={showValue} />
      ) : (
        byReceiving.map(({ rcv, rows: group }) => (
          <WorkSection
            key={rcv.receiptId}
            title={
              <>
                <JourneyName
                  vendor={rcv.vendor}
                  label={rcv.label}
                  onOpen={onOpenJourney && (() => onOpenJourney(rcv.orderId))}
                />
                <ReceivingRef receivingId={rcv.receivingId} readOnly={readOnly} />
                {!readOnly && <span className="text-xs font-normal text-muted-foreground">
                  {group.filter((r) => r.dispositionSource === 'undecided').length} to decide
                  {group.some((r) => r.dispositionSource !== 'undecided')
                    && ` · ${group.filter((r) => r.dispositionSource !== 'undecided').length} decided`}
                </span>}
              </>
            }
          >
            <DiscrepancyTable rows={group} showReceiving={false} onDecide={decide} busyKeys={busyKeys} readOnly={readOnly} showValue={showValue} />
          </WorkSection>
        ))
      )}

      {/* Received, nothing bills it — the other decision this view owns. Not a
          settle question (nothing was short), so it gets its own three verbs:
          keep the freebie, move the misfile off, or send the invoice back for
          a re-read (substitutions and mis-keys). Each ruling routes the row —
          absorbed settles it, excluded lands in Move, recapture goes to the
          orders run — so this list empties like every other queue. */}
      {investigate.length > 0 && (
        <WorkSection title="Received, nothing bills it" count={investigate.length}>
          {investigate.map((r) => {
            const busy = busyKeys.has(`${r.rcv.receiptId}:${r.key}`);
            return (
              <WorkRow
                key={`${r.rcv.receiptId}:${r.key}`}
                scrollKey={`investigate:${r.rcv.receiptId}:${r.key}`}
                title={<span className="line-clamp-1 font-medium">{r.desc}</span>}
                tone="warning"
                actions={readOnly ? undefined : busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                ) : (
                  <>
                    {/* Three rulings, one write: POST /receipts/:id/discrepancies
                        — orders:write:balance. Open journey stays for everyone. */}
                    <Can code="orders:write:balance">
                    <Hint content="Keep it; settles at no cost.">
                      <RowAction
                        onClick={() => decide(r, 'absorbed', { note: 'Freebie/extra — kept; nothing billed it.' })}
                      >
                        Freebie — keep
                      </RowAction>
                    </Hint>
                    <Hint content="Flags it as a move to another receiving.">
                      <RowAction
                        icon={<Undo2 className="h-3 w-3" />}
                        onClick={() => decide(r, 'excluded', {
                          resolutionStatus: 'pending',
                          note: 'Not this receiving — moved off from the Decide view; destination to be picked in the acot backend.',
                        })}
                      >
                        Move off
                      </RowAction>
                    </Hint>
                    <Hint content="Sends the invoice back to the run for a re-read.">
                      <RowAction
                        icon={<ScanLine className="h-3 w-3" />}
                        onClick={() => decide(r, 'recapture', {
                          note: `Received ${r.qtyReceived} of a product no invoice line bills — check for a substitution or a mis-keyed line and re-read the invoice.`,
                        })}
                      >
                        Re-read invoice
                      </RowAction>
                    </Hint>
                    </Can>
                    {onOpenJourney && <RowAction variant="quiet" onClick={() => onOpenJourney(r.rcv.orderId)}>Open journey</RowAction>}
                  </>
                )}
              >
                <ProductIdentity pid={r.pid} itemnumber={r.itemnumber} vendorSku={r.vendorSku} linked={!readOnly} className="text-xs text-muted-foreground" />
                <ReceivingRef receivingId={r.rcv.receivingId} vendor={r.rcv.vendor} readOnly={readOnly} className="text-muted-foreground" />
                <span className="text-xs tabular-nums">{r.qtyReceived} received</span>
                {r.valueAtTarget != null && <span className="text-xs tabular-nums text-muted-foreground">{money(r.valueAtTarget, r.rcv.currency)}</span>}
              </WorkRow>
            );
          })}
        </WorkSection>
      )}

      {/* Nobody's rows, in one line — so an emptier table reads as routed, not lost. */}
      {!readOnly && (elsewhere.waiting > 0 || elsewhere.agent > 0 || elsewhere.accepted > 0) && (
        <WorkFootnote>
          Elsewhere:{' '}
          {[
            elsewhere.agent > 0 ? `${elsewhere.agent} product${elsewhere.agent === 1 ? '' : 's'} with the orders run (capture/recapture)` : null,
            elsewhere.waiting > 0 ? `${elsewhere.waiting} awaiting arrival` : null,
            elsewhere.accepted > 0 ? `${elsewhere.accepted} on never-itemised vendors (accepted)` : null,
          ].filter(Boolean).join(' · ')}.
        </WorkFootnote>
      )}
    </div>
  );
}
